/**
 * Keyboard Helpers Module
 * Reusable functions for keyboard shortcuts and playback control
 */

import type { Track } from '@/api/types';
import type { PlayerUIState } from '@/stores/player';
import type { QueueItem, PlaybackQueueState } from '@/stores/playbackQueue';
import type { UseMutationResult } from '@tanstack/react-query';

/**
 * Player controls mutation interface
 */
export interface PlayerControlMutations {
  play: UseMutationResult<void, Error, { uris?: string[]; contextUri?: string; offset?: { position?: number; uri?: string }; positionMs?: number } | undefined, unknown>;
  pause: UseMutationResult<void, Error, void, unknown>;
  next: UseMutationResult<void, Error, void, unknown>;
  previous: UseMutationResult<void, Error, void, unknown>;
}

/**
 * Smart playback parameters
 * Returns either contextUri+offset (fast) or uris[] (for modified lists)
 */
export interface SmartPlaybackParams {
  contextUri?: string;
  offset?: { position: number };
  uris?: string[];
  /** True when using contextUri (Spotify manages next/previous) */
  isContextMode: boolean;
}

/**
 * Build smart playback parameters
 * Uses contextUri+offset when list is unchanged (fast, shows in Spotify)
 * Falls back to uris[] when list has been modified (preserves custom order)
 *
 * @param playlistUri - Spotify playlist URI
 * @param trackIndex - Starting track index (0-based)
 * @param trackUris - Array of track URIs from current position to end
 * @param isOrderUnchanged - Whether track order matches original
 * @returns Playback parameters for play.mutate() plus isContextMode flag
 */
export function buildSmartPlaybackParams(
  playlistUri: string | undefined,
  trackIndex: number,
  trackUris: string[],
  isOrderUnchanged: boolean
): SmartPlaybackParams {
  // Use contextUri + offset when:
  // 1. We have a playlist URI
  // 2. Track order hasn't been modified
  if (playlistUri && isOrderUnchanged) {
    return {
      contextUri: playlistUri,
      offset: { position: trackIndex },
      isContextMode: true,
    };
  }

  // Fallback to uris[] when list has been modified
  return {
    uris: trackUris,
    isContextMode: false,
  };
}

/**
 * Build hookmode queue items from tracks
 * Extracts URI and duration for 15s preview playback
 */
export function buildHookmodeQueue(tracks: Track[]): QueueItem[] {
  return tracks.map((track) => ({
    uri: track.uri,
    id: track.id,
    durationMs: track.durationMs,
  }));
}

/**
 * Build normal playback queue items from tracks
 * Includes ID for tracking current playback position
 */
export function buildPlaybackQueue(tracks: Track[]): QueueItem[] {
  return tracks.map((track) => ({
    uri: track.uri,
    id: track.id,
    durationMs: track.durationMs,
  }));
}

/**
 * Play a hookmode track with 15s preview and auto-advance
 * Starts playback at center of track and sets timer to advance after 15 seconds
 *
 * @param hookItem - Track to play with URI and duration
 * @param playerUIStore - Player UI store instance
 * @param playbackQueueStore - Playback queue store instance
 * @param mutations - Player control mutations (play, pause)
 */
export function playHookmodeTrack(
  hookItem: QueueItem,
  playerUIStore: PlayerUIState,
  playbackQueueStore: PlaybackQueueState,
  mutations: Pick<PlayerControlMutations, 'play' | 'pause'>
): void {
  const hookStart = Math.floor(hookItem.durationMs / 2 - 7500); // 15s centered
  const hookDuration = 15000; // 15 seconds

  // Clear any existing timer
  playbackQueueStore.clearHookTimer();

  // Start playback at hook position
  mutations.play.mutate({
    uris: [hookItem.uri],
    positionMs: Math.max(0, hookStart),
  });

  // Set timer to auto-advance after 15s
  const timerId = setTimeout(() => {
    const nextItem = playbackQueueStore.moveNext();
    if (nextItem) {
      playHookmodeTrack(nextItem, playerUIStore, playbackQueueStore, mutations);
    } else {
      // End of queue - pause playback
      mutations.pause.mutate();
    }
  }, hookDuration);

  playbackQueueStore.setHookTimer(timerId);
}

/**
 * Get next track ID in array
 * Returns null if current track is last or not found
 */
export function getNextTrackId(currentId: string, allIds: string[]): string | null {
  const currentIndex = allIds.findIndex((id) => id === currentId);
  if (currentIndex === -1 || currentIndex >= allIds.length - 1) {
    return null;
  }
  return allIds[currentIndex + 1] ?? null;
}

/**
 * Get previous track ID in array
 * Returns null if current track is first or not found
 */
export function getPreviousTrackId(currentId: string, allIds: string[]): string | null {
  const currentIndex = allIds.findIndex((id) => id === currentId);
  if (currentIndex <= 0) {
    return null;
  }
  return allIds[currentIndex - 1] ?? null;
}

/**
 * Scroll track row into view
 * Uses data-track-id attribute to find and scroll to track row element
 *
 * @param trackId - Track ID to scroll to
 * @param behavior - Scroll behavior ('smooth' or 'instant')
 */
export function scrollToTrack(
  trackId: string,
  behavior: ScrollBehavior = 'smooth'
): void {
  const trackRow = document.querySelector(`[data-track-id="${trackId}"]`);
  if (trackRow) {
    trackRow.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: 'nearest',
    });
  }
}
